import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HandshakeIcon from "@mui/icons-material/Handshake";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import CampaignIcon from "@mui/icons-material/Campaign";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PublicIcon from "@mui/icons-material/Public";
import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from '@mui/icons-material/Info';
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import StatusChip, { ReportLogBrandChip } from "../StatusChip";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import {
  STATUS_META,
  formatLeadDateTime,
  formatCurrency,
  formatDuration,
  isReportableStatus,
  getScoreMeta,
  getQualifiedMeta,
  SCORING_STATUS_META,
  REPORT_LOG_STATUS_META,
} from "../constants";
import { formatAcquiredDate } from "./LeadsTable";

interface LeadDetailDrawerProps {
  data: LeadsManagementData;
}

type DetailTabKey = "details" | "scoring" | "log";

// Shared shell for every collapsible section in the DETAILS tab (BRAND
// OUTCOME/CONTACT/BUSINESS & LOAN/ATTRIBUTION/IP ADDRESS) — an Accordion
// instead of an always-expanded card, each one independently
// expanded/collapsed, matching the target design.
function DetailAccordion({
  icon,
  title,
  headerRight,
  defaultExpanded,
  children,
}: {
  icon?: ReactNode;
  title: string;
  headerRight?: ReactNode;
  defaultExpanded?: boolean;
  children: ReactNode;
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${theme.palette.grey[400]}`,
        borderRadius: "10px !important",
        overflow: "hidden",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: "12px",
          bgcolor: "grey.50",
          minHeight: "44px !important",
          "& .MuiAccordionSummary-content": { my: "11px" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            {icon && <Box sx={{ display: "flex", color: "text.disabled", flexShrink: 0 }}>{icon}</Box>}
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: 13,
                lineHeight: "22px",
                letterSpacing: "0.46px",
                textTransform: "uppercase",
                color: "grey.900",
              }}
            >
              {title}
            </Typography>
          </Box>
          {headerRight && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{headerRight}</Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: "12px", pt: 0, pb: "8px" }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export type RationaleBlock =
  | { type: "bullet"; passed: boolean; title: string; body: string }
  | { type: "action"; title: string; body: string }
  | { type: "text"; body: string };

// lead_scoring_description/loan_approval_likelihood_description come back as
// one free-text string, but in practice it's laid out as blank-line-
// separated factors — each starting with a ✓/✗ (or ✔/✘/✅/❌) mark followed
// by its title, then a description either inline after a colon or on the
// next line(s) — plus a trailing "Recommended action"/"Action" paragraph.
// This splits that back into typed blocks so each can get its own icon/
// color; anything that doesn't match either shape falls back to plain text
// rather than being dropped.
export function parseRationaleBlocks(text: string | null): RationaleBlock[] {
  if (!text) return [];
  return text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    // An "ACTION:" line isn't always preceded by a blank line — split it (and
    // everything after it) into its own block so it still gets the callout.
    .flatMap((block) => {
      const lines = block.split("\n");
      const actionIndex = lines.findIndex(
        (line, index) => index > 0 && /^\s*(?:recommended\s+action|action)\s*:/i.test(line),
      );
      if (actionIndex === -1) return [block];
      return [lines.slice(0, actionIndex).join("\n").trim(), lines.slice(actionIndex).join("\n").trim()];
    })
    .map((block) => {
      const [firstLine, ...rest] = block.split("\n").map((line) => line.trim());

      const bulletMatch = firstLine.match(/^([✓✔✅✗✘❌])\s*(.*?):?\s*$/);
      if (bulletMatch) {
        return {
          type: "bullet",
          passed: ["✓", "✔", "✅"].includes(bulletMatch[1]),
          title: bulletMatch[2],
          body: rest.join(" "),
        };
      }

      const actionMatch = firstLine.match(/^(?:recommended\s+action|action)\s*:?\s*(.*)$/i);
      if (actionMatch) {
        return {
          type: "action",
          title: "RECOMMENDED ACTION",
          body: [actionMatch[1], ...rest].filter(Boolean).join(" "),
        };
      }

      return { type: "text", body: block };
    });
}

// Plain-text rationale lines sometimes start with a 🔴/🟢 status dot instead
// of a ✓/✗ mark — swap just that dot for the matching red cross / green check
// icon and leave the line's text exactly as written.
// A dot sitting alone on its line is carried onto the next line of text, so
// the icon always shares a line with the sentence it marks. Lines following
// a marked line are flagged `indent` so they line up under its text (hanging
// indent) until the next marked line or a blank line.
export function toRationaleLines(
  body: string,
): Array<{ passed: boolean | null; text: string; indent: boolean }> {
  const lines: Array<{ passed: boolean | null; text: string; indent: boolean }> = [];
  let pendingDot: boolean | null = null;
  let inFactor = false;
  body.split("\n").forEach((line) => {
    const match = line.match(/^\s*(🔴|🟢)\s*/u);
    const passed = match ? match[1] === "🟢" : null;
    const text = match ? line.slice(match[0].length) : line;
    if (passed !== null && text.trim() === "") {
      pendingDot = passed;
      return;
    }
    if (pendingDot !== null && passed === null && text.trim() !== "") {
      lines.push({ passed: pendingDot, text, indent: false });
      pendingDot = null;
      inFactor = true;
      return;
    }
    if (passed !== null) {
      lines.push({ passed, text, indent: false });
      inFactor = true;
      return;
    }
    if (text.trim() === "") inFactor = false;
    lines.push({ passed: null, text, indent: inFactor });
  });
  if (pendingDot !== null) lines.push({ passed: pendingDot, text: "", indent: false });
  return lines;
}

// Label/value row styling shared by every DETAILS-tab section (SectionCard
// and the IP ADDRESS section), so they stay visually identical.
const FIELD_ROW_SX = {
  display: "grid",
  gridTemplateColumns: "128px 1fr",
  columnGap: 1.5,
  pt: "8px",
  pb: "2px",
  alignItems: "center",
};

const FIELD_LABEL_SX = {
  fontFamily: "Roboto",
  fontWeight: 500,
  fontSize: "10px",
  lineHeight: "14px",
  letterSpacing: "0px",
  textTransform: "uppercase" as const,
  color: "text.secondary",
};

const FIELD_VALUE_SX = {
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "166%",
  letterSpacing: "0.4px",
  color: "text.primary",
};

const FIELD_DIVIDER_SX = { borderTop: "1px solid", borderColor: "grey.300" };

const RATIONALE_HEADING_SX = {
  fontFamily: "Roboto",
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "24px",
  letterSpacing: "0.4px",
  textTransform: "uppercase" as const,
};

// LEAD SCORING RATIONALE / LOAN APPROVAL LIKELIHOOD RATIONALE — same
// DetailAccordion shell, with the numeric score + qualitative band chip
// (Strong/Moderate/Weak, from getScoreMeta) in the header instead of an
// icon. The body renders each parsed factor as a check/✗ + heading + short
// description, and rebrands whatever the source calls its closing
// recommendation paragraph as a "RECOMMENDED ACTION" callout.
function ScoringRationaleAccordion({
  title,
  score,
  rationale,
  defaultExpanded,
}: {
  title: string;
  score: number;
  rationale: string | null;
  defaultExpanded?: boolean;
}) {
  const meta = getScoreMeta(score);
  const blocks = parseRationaleBlocks(rationale);
  return (
    <DetailAccordion
      title={title}
      defaultExpanded={defaultExpanded}
      headerRight={
        <>
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "24px",
              letterSpacing: "0.17px",
              color: "text.primary",
            }}
          >
            Score: {score.toFixed(1)}/10
          </Typography>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: meta.bg,
              color: meta.color,
              fontFamily: "Roboto",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {meta.band}
          </Box>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {blocks.map((block, index) => {
          if (block.type === "bullet") {
            const color = block.passed ? "success.main" : "error.dark";
            const Icon = block.passed ? CheckIcon : CloseIcon;
            return (
              <Box key={index}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Icon sx={{ fontSize: 18, color }} />
                  <Typography sx={{ ...RATIONALE_HEADING_SX, color }}>{block.title}</Typography>
                </Box>
                {block.body && (
                  <Typography sx={{ fontFamily: "Roboto", fontSize: 13.5, color: "grey.700", mt: 0.5, ml: 3.25 }}>
                    {block.body}
                  </Typography>
                )}
              </Box>
            );
          }

          if (block.type === "action") {
            return (
              <Box key={index} sx={{ bgcolor: "#E8F4FD", border: "1px solid #BBDEFB", borderRadius: 2, p: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <InfoIcon sx={{ fontSize: 18, color: "info.dark" }} />
                  <Typography sx={{ ...RATIONALE_HEADING_SX, color: "info.dark" }}>{block.title}</Typography>
                </Box>
                <Typography sx={{ fontFamily: "Roboto", fontSize: 13.5, color: "info.dark", mt: 0.5, ml: 3.25 }}>
                  {block.body}
                </Typography>
              </Box>
            );
          }

          return (
            <Typography
              key={index}
              component="div"
              sx={{ fontFamily: "Roboto", fontSize: 14, color: "#333", lineHeight: 1.62, whiteSpace: "pre-wrap" }}
            >
              {toRationaleLines(block.body).map(({ passed, text, indent }, lineIndex) => {
                if (passed === null) {
                  return (
                    <Box key={lineIndex} sx={{ pl: indent ? "24px" : 0 }}>
                      {text}
                    </Box>
                  );
                }
                const DotIcon = passed ? CheckIcon : CloseIcon;
                return (
                  <Box key={lineIndex} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    <DotIcon sx={{ fontSize: 18, mt: "3px", flexShrink: 0, color: passed ? "success.main" : "error.dark" }} />
                    <span>{text}</span>
                  </Box>
                );
              })}
            </Typography>
          );
        })}
      </Box>
    </DetailAccordion>
  );
}

function SectionCard({
  title,
  icon,
  fields,
  defaultExpanded,
}: {
  title: string;
  icon: ReactNode;
  fields: Array<[string, string]>;
  defaultExpanded?: boolean;
}) {
  return (
    <DetailAccordion icon={icon} title={title} defaultExpanded={defaultExpanded}>
      {fields.map(([label, value], index) => (
        <Box key={label} sx={{ ...FIELD_ROW_SX, ...(index === 0 ? {} : FIELD_DIVIDER_SX) }}>
          <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
          <Typography sx={{ ...FIELD_VALUE_SX, wordBreak: "break-all" }}>{value}</Typography>
        </Box>
      ))}
    </DetailAccordion>
  );
}

function EmptyState({ icon, message }: { icon: ReactNode; message: string }) {
  return (
    <Box sx={{ bgcolor: "common.white", border: "1px solid #ececec", borderRadius: 2.5, p: "44px 20px", textAlign: "center" }}>
      {icon}
      <Typography sx={{ fontFamily: "Roboto", fontSize: 14.5, color: "grey.600", mt: 1 }}>{message}</Typography>
    </Box>
  );
}

export default function LeadDetailDrawer({ data }: LeadDetailDrawerProps) {
  const {
    detailLead,
    closeLeadDetail,
    reportSingleLead,
    isReporting,
    canReportLeads,
    goToFilteredLeads,
  } = data;
  const theme = useTheme();
  const brandColor = theme.palette.secondary.main;
  const [activeTab, setActiveTab] = useState<DetailTabKey>("details");

  // The drawer stays mounted across different leads (only `detailLead`
  // changes), so without this the tab you were on for the previous lead
  // would stick around when opening a different one.
  useEffect(() => {
    setActiveTab("details");
  }, [detailLead?.id]);

  if (!detailLead) return null;

  const meta = STATUS_META[detailLead.status];
  const qualifiedMeta = getQualifiedMeta(detailLead.qualifiedByBrand);
  const reportable = isReportableStatus(detailLead.status);

  // Short outcome line next to INCOME FROM BRAND — only for terminal report
  // states (a lead still Pending/Initial/etc. hasn't been reported yet, so
  // there's nothing to report about there).
  const reportOutcomeMessage =
    detailLead.status === "Success"
      ? { text: "Reported Successfully to Brand", color: STATUS_META.Success.color }
      : detailLead.status === "Failure" || detailLead.status === "BrandDuplication"
      ? { text: "This lead cannot be reported.", color: STATUS_META.Failure.color }
      : detailLead.status === "Rejected"
      ? { text: "Rejected", color: STATUS_META.Rejected.color }
      : null;

  const sections: Array<{ title: string; icon: ReactNode; fields: Array<[string, string]> }> = [
    {
      title: "BRAND OUTCOME",
      icon: <HandshakeIcon sx={{ fontSize: 19 }} />,
      fields: [
        ["Brand lead status", detailLead.brandLeadStatus ?? "—"],
        ["Qualified by brand", qualifiedMeta.label],
        ["Income from brand", formatCurrency(detailLead.incomeFromBrand)],
        ["Reported at", formatLeadDateTime(detailLead.reportedAt)],
        ["Report attempts", String(detailLead.reportLog.length)],
      ],
    },
    {
      title: "CONTACT",
      icon: <PersonIcon sx={{ fontSize: 19 }} />,
      fields: [
        ["Full name", detailLead.name],
        ["Email", detailLead.email],
        ["Phone", detailLead.phone],
        ["Zip code", detailLead.zip],
      ],
    },
    {
      title: "BUSINESS & LOAN",
      icon: <StoreIcon sx={{ fontSize: 19 }} />,
      fields: [
        ["Business name", detailLead.businessName],
        ["Business industry", detailLead.industry],
        ["Time in business", detailLead.timeInBusiness],
        ["Monthly revenue", detailLead.revenue],
        ["Credit rating", detailLead.credit],
        ["Borrow amount", detailLead.amount],
        ["Loan purpose", detailLead.loanPurpose],
      ],
    },
    {
      title: "ATTRIBUTION",
      icon: <CampaignIcon sx={{ fontSize: 19 }} />,
      fields: [
        ["User unique ID", detailLead.userUniqueId],
        ["Traffic source", detailLead.trafficSource],
        ["Ad ID", detailLead.adId],
        ["Ad platform click ID", detailLead.adPlatformClickId],
        ["Date of acquisition", formatLeadDateTime(detailLead.date)],
      ],
    },
  ];

  // duplicate_ip_leads is scoped to the entire brand_leads table by the API
  // (not just the currently loaded page), and already excludes the lead
  // itself — see mapApiLeadToLead in constants.ts.
  const ipDupes = detailLead.duplicateIpLeads;

  const scoringStatus = detailLead.scoringStatus;
  const hasScoring =
    scoringStatus === "completed" &&
    detailLead.leadScore !== null &&
    detailLead.approvalLikelihood !== null;
  const noScoringMessage =
    scoringStatus === "processing"
      ? "Scoring is still processing for this lead — check back shortly."
      : scoringStatus === "failed"
        ? "The scoring run failed for this lead. No score is available."
        : "No scoring data is available for this lead yet.";

  const reportLog = detailLead.reportLog;

  const tabStyles = {
    minHeight: 0,
    "& .MuiTab-root": {
      minHeight: 0,
      px: 2,
      py: 1,
      fontSize: 13,
      fontWeight: 500,
      letterSpacing: "0.5px",
      color: "#6b6b6b",
    },
    "& .Mui-selected": { color: `${brandColor} !important` },
    "& .MuiTabs-indicator": { backgroundColor: "secondary.main", height: 3 },
  };

  return (
    <Drawer
      anchor="right"
      open
      onClose={closeLeadDetail}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 200 }}
      PaperProps={{
        sx: { width: 600, maxWidth: "96vw", zIndex: (theme) => theme.zIndex.appBar + 200 },
      }}
      slotProps={{ backdrop: { onClick: closeLeadDetail } }}
    >
      <Box sx={{ p: "16px !important" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: 20,
                lineHeight: "160%",
                letterSpacing: "0.15px",
                color: "#1a1a1a",
              }}
            >
              {detailLead.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "143%",
                letterSpacing: "0.17px",
                color: "grey.500",
                mt: 0.375,
              }}
            >
              Lead: #{detailLead.id}
              <Box component="span" sx={{ display: "inline-block", width: 16 }} />
              Assigned To: {detailLead.brand}
            </Typography>
          </Box>
          <IconButton onClick={closeLeadDetail} sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.25 }}>
          <StatusChip label={meta.label} />
          <StatusChip label={qualifiedMeta.label} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "143%",
              letterSpacing: "0.15px",
              color: "text.secondary",
            }}
          >
            INCOME FROM BRAND
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "157%",
              letterSpacing: "0.1px",
              verticalAlign: "middle",
              color: detailLead.incomeFromBrand ? "success.main" : "grey.500",
            }}
          >
            {formatCurrency(detailLead.incomeFromBrand)}
          </Typography>
          {reportOutcomeMessage && (
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "143%",
                letterSpacing: "0.17px",
                ml: "auto",
                color: reportOutcomeMessage.color,
              }}
            >
              {reportOutcomeMessage.text}
            </Typography>
          )}
          {reportable && (
            <Button
              size="small"
              variant="contained"
              disabled={!canReportLeads || isReporting}
              onClick={() => reportSingleLead(detailLead.id)}
              data-tracker="930"
              sx={{ ml: "auto", bgcolor: "secondary.main", "&:hover": { bgcolor: "secondary.dark" } }}
            >
              {isReporting ? "Reporting…" : "Report to brand"}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: "1px solid #ececec" }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={tabStyles}>
          <Tab value="details" label="DETAILS" />
          <Tab value="scoring" label="SCORING" data-tracker="966" />
          <Tab value="log" label={`REPORT LOG (${reportLog.length})`} data-tracker="1038" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "common.white" }}>
        {activeTab === "details" && (
          <Box sx={{ p: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 1 }}>
            {sections.map((section, index) => (
              <SectionCard
                key={section.title}
                title={section.title}
                icon={section.icon}
                fields={section.fields}
                defaultExpanded={index === 0}
              />
            ))}

            <DetailAccordion icon={<PublicIcon sx={{ fontSize: 19 }} />} title="IP ADDRESS">
              <Box sx={FIELD_ROW_SX}>
                <Typography sx={FIELD_LABEL_SX}>IP address</Typography>
                <Typography sx={FIELD_VALUE_SX}>{detailLead.ipAddress ?? "—"}</Typography>
              </Box>
              <Box sx={{ ...FIELD_ROW_SX, ...FIELD_DIVIDER_SX, alignItems: "flex-start" }}>
                <Typography sx={{ ...FIELD_LABEL_SX, pt: "3px" }}>Duplicate submissions</Typography>
                {ipDupes.length > 0 ? (
                  <Box>
                    <Typography sx={FIELD_VALUE_SX}>{ipDupes.length} shared:</Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                      {ipDupes.map((dup) => (
                        <Box key={dup.id} sx={{ bgcolor: "#9C27B00A", borderRadius: "8px", px: "12px", py: "8px" }}>
                          <Typography
                            component="span"
                            onClick={() => goToFilteredLeads({ search: String(dup.id) })}
                            title="Search for this lead by ID"
                            data-tracker="964"
                            sx={{
                              ...FIELD_VALUE_SX,
                              color: "secondary.main",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            #{dup.id} {dup.fullName}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                            <Typography sx={{ ...FIELD_VALUE_SX, color: "text.secondary", wordBreak: "break-all" }}>
                              {dup.email ?? ""}
                            </Typography>
                            <Typography sx={{ ...FIELD_VALUE_SX, color: "text.disabled", whiteSpace: "nowrap" }}>
                              Reported: {formatAcquiredDate(dup.created)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    {ipDupes.length > 1 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                        <Typography
                          onClick={() => detailLead.ipAddress && goToFilteredLeads({ search: detailLead.ipAddress })}
                          data-tracker="965"
                          sx={{ ...FIELD_VALUE_SX, fontWeight: 500, color: "secondary.main", cursor: "pointer" }}
                        >
                          See all leads from this IP
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ ...FIELD_VALUE_SX, color: "text.secondary" }}>
                    No other lead was submitted from this IP address.
                  </Typography>
                )}
              </Box>
            </DetailAccordion>
          </Box>
        )}

        {activeTab === "scoring" && (
          <Box sx={{ p: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 1 }}>
            {hasScoring ? (
              <>
                <ScoringRationaleAccordion
                  title="LEAD SCORING RATIONALE"
                  score={detailLead.leadScore!}
                  rationale={detailLead.scoreRationale}
                  defaultExpanded
                />
                <ScoringRationaleAccordion
                  title="LOAN APPROVAL LIKELIHOOD RATIONALE"
                  score={detailLead.approvalLikelihood!}
                  rationale={detailLead.approvalRationale}
                />
              </>
            ) : (
              <EmptyState icon={<HourglassEmptyIcon sx={{ fontSize: 38, color: "#d6c2de" }} />} message={noScoringMessage} />
            )}

            <DetailAccordion
              title="SCORING RUN"
              headerRight={
                scoringStatus ? (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: SCORING_STATUS_META[scoringStatus].bg,
                      color: SCORING_STATUS_META[scoringStatus].color,
                      fontFamily: "Roboto",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {SCORING_STATUS_META[scoringStatus].label}
                  </Box>
                ) : undefined
              }
            >
              <Box sx={FIELD_ROW_SX}>
                <Typography sx={FIELD_LABEL_SX}>Updated</Typography>
                <Typography sx={FIELD_VALUE_SX}>{formatLeadDateTime(detailLead.scoringUpdatedAt ?? null)}</Typography>
              </Box>
              <Box sx={{ ...FIELD_ROW_SX, ...FIELD_DIVIDER_SX }}>
                <Typography sx={FIELD_LABEL_SX}>Questionnaire fill time</Typography>
                <Typography sx={FIELD_VALUE_SX}>{formatDuration(detailLead.questionnaireFillTimeSeconds)}</Typography>
              </Box>
            </DetailAccordion>
          </Box>
        )}

        {activeTab === "log" && (
          <Box sx={{ p: "24px 16px 16px", display: "flex", flexDirection: "column", gap: 1.75 }}>
            {reportLog.length === 0 ? (
              <EmptyState
                icon={<ReceiptLongIcon sx={{ fontSize: 38, color: "#d6c2de" }} />}
                message="No report attempts recorded for this lead yet."
              />
            ) : (
              reportLog.map((log) => {
                const logMeta = REPORT_LOG_STATUS_META[log.status];
                return (
                  <Box key={log.id} >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Typography sx={{ fontFamily: "Roboto", fontSize: 16, fontWeight: 500, color: "#1a1a1a" }}>
                        Attempt #{log.attempt}
                      </Typography>
                      <ReportLogBrandChip brand={log.brand} />
                      <StatusChip label={logMeta.label} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: 12,
                          lineHeight: "166%",
                          letterSpacing: "0.4px",
                          color: "grey.600",
                        }}
                      >
                        Response PAYLOAD: log id {log.id}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: 12,
                          lineHeight: "166%",
                          letterSpacing: "0.4px",
                          color: "grey.500",
                        }}
                      >
                        {formatLeadDateTime(log.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 0.75, "& span": { color: "#00000099 !important"} }}>
                      <SyntaxHighlighter
                        language="json"
                        style={materialLight}
                        showLineNumbers
                        wrapLongLines
                        customStyle={{
                          margin: 0,
                          borderRadius: 8,
                          border: `1px solid ${theme.palette.common.black}`,
                          fontSize: 12,
                          maxHeight: 200,
                          overflow: "auto",
                          backgroundColor: theme.palette.common.white,
                        }}
                      >
                        {log.payload}
                      </SyntaxHighlighter>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </Box>

    </Drawer>
  );
}
